import { CryptorsAdmin } from "@cryptors/api-client";
import DownloadIcon from "@mui/icons-material/Download";
import QuizIcon from "@mui/icons-material/Quiz";
import { Box, Button, IconButton } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  EditButton,
  ShowButton,
  List,
  DateField,
  ListButton,
  useDataGrid,
} from "@refinedev/mui";
import axios from "axios";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DeleteIconButton } from "@/components/forms/DeleteIconButton";
import { SearchField } from "@/components/forms/SearchField";

const PAGE_SIZE = 500;

const toCsv = <TRow extends object>(rows: TRow[]) => {
  if (!rows.length) return "";
  const cols = ["id", "name", "reward", "archived", "draft", "createdAt"];
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const body = rows
    .map((r) => cols.map((c) => escape(r[c as keyof TRow])).join(","))
    .join("\n");
  return `${header}\n${body}`;
};

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

type Page = { data: CryptorsAdmin; total: number };

const fetchAllCryptors = async (): Promise<CryptorsAdmin> => {
  const fetchPage = async (page: number): Promise<Page> => {
    const res = await axios.get<Page | CryptorsAdmin>(
      `/admin/cryptors?limit=${PAGE_SIZE}&page=${page}`,
    );
    if (Array.isArray(res.data)) {
      return { data: res.data, total: res.data.length };
    }
    return res.data;
  };
  const first = await fetchPage(1);
  const pageCount = Math.max(1, Math.ceil(first.total / PAGE_SIZE));
  if (pageCount <= 1) return first.data;
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) => fetchPage(i + 2)),
  );
  return [first.data, ...rest.map((p) => p.data)].flat() as CryptorsAdmin;
};

const buildSearchFilters = (q: string): CrudFilter[] => {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const orValue: CrudFilter[] = [
    { field: "name", operator: "containss", value: trimmed },
  ];
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    orValue.push({ field: "id", operator: "eq", value: n });
    orValue.push({ field: "reward", operator: "eq", value: n });
  }
  return [{ operator: "or", value: orValue }];
};

export const CryptorList: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();

  const { dataGridProps, setFilters } = useDataGrid<CryptorsAdmin[number]>({
    resource: "cryptors",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "createdAt", order: "desc" }] },
  });

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = await fetchAllCryptors();
      downloadCsv(
        `cryptors-${new Date().toISOString().slice(0, 10)}.csv`,
        toCsv(all),
      );
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<GridColDef<CryptorsAdmin[number]>[]>(
    () => [
      {
        field: "id",
        headerName: "Id",
        type: "number",
        minWidth: 50,
      },
      {
        field: "name",
        flex: 1,
        headerName: "Nom",
        minWidth: 200,
      },
      {
        field: "image.url",
        headerName: "Image",
        sortable: false,
        minWidth: 50,
        valueGetter: (_value, row) => row.image?.url,
        renderCell: function render({ value }) {
          return <img src={value} alt="Cryptor" width="50" />;
        },
      },
      {
        field: "reward",
        headerName: "Récompense",
        type: "number",
        minWidth: 50,
      },
      {
        field: "createdAt",
        headerName: "Date de création",
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        display: "flex",
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
      },
      {
        field: "quiz",
        headerName: "Quiz",
        sortable: false,
        width: 50,
        renderCell: function render({ row }) {
          return (
            <>
              <IconButton onClick={() => navigate(`/cryptors/${row.id}/quiz`)}>
                <QuizIcon />
              </IconButton>
            </>
          );
        },
      },
      {
        field: "chat",
        headerName: "Chat",
        sortable: false,
        width: 50,
        renderCell: function render({ row }) {
          return (
            <>
              <ListButton
                hideText
                onClick={() => {
                  navigate(`/cryptors/${row.id}/chat-message`);
                }}
              />
            </>
          );
        },
      },
      {
        field: "archived",
        headerName: "Archivé",
        type: "boolean",
        width: 70,
      },
      {
        field: "draft",
        headerName: "Publié",
        type: "boolean",
        width: 70,
        valueGetter: (_value, row) => !row.draft,
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteIconButton
                recordItemId={row.id}
                confirmTitle="Etes-vous sûr de vouloir supprimer ce cryptor ? Cette action est définitive et entraînera la suppression de tous les points accumulés par les utilisateurs."
              />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
    ],
    [navigate],
  );

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Export…" : "Export CSV"}
          </Button>
        </>
      )}
    >
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (nom, id, récompense)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
